import React from 'react'
import { graphql } from 'gatsby'
import Layout from '@components/Layout'
import PracticeAreasDetailContent from '@sections/PracticeAreasDetailContent'
import { getHrefLangUrls, handleSEO } from '@utils'
import TitleAndMetaTags from '@components/TitleAndMetaTags'

const PracticeAreasDetail = ({ data, pageContext }) => {
  return (
    <Layout {...pageContext} contentType="practiceArea">
      <PracticeAreasDetailContent
        practiceArea={data?.practiceArea}
        professionals={data?.professionals?.nodes}
        news={data?.news?.nodes}
        events={data?.events?.nodes}
        insights={data?.insights?.nodes}
        parentPath={data?.parentPage?.slug}
        accolades={data?.accolades?.nodes}
      />
    </Layout>
  )
}

export default PracticeAreasDetail

export const query = graphql`
  query PracticeAreaDetailV2(
    $node_locale: String!
    $contentful_id: String!
    $displayOnSitesAsNodeLocales: [String!] # $professionalCardImageWidth: Int = 130
  ) {
    parentPage: contentfulPage(
      contentful_id: { eq: "23hSKEtDWwHSV3Gjx8rCzU" }
      node_locale: { eq: $node_locale }
    ) {
      ...basePageFields
      parentId: contentful_id
    }
    practiceArea: contentfulPracticeArea(
      node_locale: { eq: $node_locale }
      contentful_id: { eq: $contentful_id }
    ) {
      contentful_id
      title
      customHtmlComponent
      # heroCarousel {
      #   ...moduleCarousel
      # }
      descriptionHtml {
        useHTMLField
        html
      }
      prosOff
      newsOff
      insightsOff
      content {
        raw
        references {
          ...assetBase
          ...articleLinkFields
          ...articleCategoryLinkFields
          ...eventLinkFields
          ...filterPageLinkFields
          ...newsLinkFields
          ...officeLinkFields
          ...pageLinkFields
          ...practiceAreaLinkFields
          ...professionalLinkFields
          ...moduleAccordion
          ...moduleDisclaimer
          ...moduleMediaEmbed
          ...moduleList
          # ...componentMediaColumns
        }
      }
      headerBackgroundImage {
        ...assetBase
      }
      experienceOverride {
        ...baseNewsFields
      }
      upcomingEventsOverride {
        ...baseEventFields
      }
      newsAndInsightsOverride {
        ...baseNewsFields
        ...baseArticleFields
      }
      recognitionsOverride {
        ...baseArticleFields
      }
      practiceChairs {
        ...componentChair
      }
      featuredCallouts {
        ...moduleMediaEmbed
        ...moduleList
      }
      relatedPracticeAreas {
        ...practiceArea
      }
      seo {
        ...seoMetadata
      }
    }
    professionals: allContentfulProfessional(
      filter: {
        node_locale: { eq: $node_locale }
        practiceAreas: { elemMatch: { contentful_id: { eq: $contentful_id } } }
        active: { eq: true }
      }
    ) {
      nodes {
        ...professionalFields
        firstName
        lastName
        relatedTitle {
          title
          titleGroup {
            title
            sortOrder
          }
        }
      }
    }
    insights: allContentfulArticle(
      filter: {
        node_locale: { eq: $node_locale }
        practiceAreas: { elemMatch: { contentful_id: { eq: $contentful_id } } }
        practiceOverride: { ne: true }
      }
      sort: [{ publicationDate: DESC }, { createdAt: ASC }, { title: ASC }]
    ) {
      nodes {
        ...insights
      }
    }
    news: allContentfulNews(
      filter: {
        node_locale: { eq: $node_locale }
        associatedPracticeAreas: {
          elemMatch: { contentful_id: { eq: $contentful_id } }
        }
      }
      sort: [{ publicationDate: DESC }, { createdAt: ASC }, { title: ASC }]
    ) {
      nodes {
        ...news
      }
    }
    events: allContentfulEvent(
      filter: {
        node_locale: { eq: $node_locale }
        associatedPracticeAreas: {
          elemMatch: { contentful_id: { eq: $contentful_id } }
        }
      }
      sort: [{ eventStart: DESC }, { createdAt: ASC }, { title: ASC }]
    ) {
      nodes {
        ...events
      }
    }
    accolades: allContentfulAccolade(
      filter: {
        node_locale: { eq: $node_locale }
        practiceAreas: { elemMatch: { contentful_id: { eq: $contentful_id } } }
        practiceOverride: { ne: true }
      }
      sort: [{ publicationDate: DESC }, { createdAt: ASC }, { title: ASC }]
    ) {
      nodes {
        ...baseAccoladeFields
      }
    }
    currentPageWithAllLocales: allContentfulPracticeArea(
      filter: {
        node_locale: { in: $displayOnSitesAsNodeLocales }
        contentful_id: { eq: $contentful_id }
      }
    ) {
      nodes {
        slug
        node_locale
      }
    }
  }
`

export const Head = ({
  data: { practiceArea, currentPageWithAllLocales },
  pageContext: { displayLocale, parentPages, localizedSlug },
}) => {
  const {
    title,
    descriptionHtml,
    content,
    relatedPracticeAreas,
    seo,
  } = practiceArea

  const { seoTitle, seoDescription, seoKeywords, seoImage } = handleSEO(
    seo,
    {
      title: title,
      descriptionHtml: descriptionHtml,
      content: content,
      practiceAreas: relatedPracticeAreas,
    },
    displayLocale,
    'practiceArea'
  )

  const hrefLangUrls = getHrefLangUrls(
    currentPageWithAllLocales?.nodes,
    parentPages
  )

  return (
    <TitleAndMetaTags
      hrefLangUrls={hrefLangUrls}
      title={seoTitle}
      description={seoDescription}
      keywords={seoKeywords}
      image={seoImage}
      canonicalSlug={localizedSlug}
      usDefault={seo?.enUsCanonicalUrl}
      noIndex={seo?.noIndex}
      noArchive={seo?.noArchive}
    />
  )
}
